import { Control, FieldErrors, Controller } from 'react-hook-form';

import { useQuery } from '@tanstack/react-query';
import { UserKeys, getAllGroupTemplates } from '../../../../api/users-api';
import { sortGroupTemplatesByName } from '../../../../shared/group-settings.config';
import { Messages } from '../../../../utils/message-constants';
import { RulesEngineFormValues } from '../rules-engine-form-page';
import ActionDescription from './action-description';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface ApplyGroupTemplateActionProps {
  description?: string;
  control: Control<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
}

export default function ApplyGroupTemplateAction({
  control,
  errors,
  description,
}: ApplyGroupTemplateActionProps) {
  const { data: groupTemplates, isLoading: templateLoading } = useQuery({
    queryKey: [UserKeys.GroupTemplates],
    queryFn: () => getAllGroupTemplates(),
    select: sortGroupTemplatesByName,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group templates'),
    },
  });

  return (
    <Stack sx={{ p: 1 }}>
      <ActionDescription description={description} />
      {templateLoading ? (
        <Skeleton variant="rounded" width={400} height={56} />
      ) : (
        <Box sx={{ width: 400 }}>
          <Controller
            name="groupSettingsInfo.groupTemplateId"
            control={control}
            render={({ field }) => {
              const selectedTemplate =
                groupTemplates?.find((t) => t.id === field.value) || null;
              return (
                <Autocomplete
                  options={groupTemplates || []}
                  getOptionLabel={(option) => option.groupTemplateName}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  value={selectedTemplate}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.id : 0);
                  }}
                  disabled={groupTemplates?.length === 0}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        groupTemplates?.length === 0
                          ? 'No group templates available'
                          : 'Group Template'
                      }
                      error={!!errors.groupSettingsInfo?.groupTemplateId}
                      helperText={
                        errors?.groupSettingsInfo?.groupTemplateId?.message ||
                        ' '
                      }
                    />
                  )}
                />
              );
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
